import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Check, Plus, Upload } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import { apiGet, apiPatchAsUser, apiPost, apiPostAsUser, responseErrorMessage } from '../../lib/api_v2';
import db, { clearFingerprint } from '../../lib/db';
import { assignDocument } from '../../lib/desktop_navigation_diagnostics';
import { logError } from '../../lib/logger';
import { enqueueUpload, removeMediaAttachment, waitForMediaAttachmentUpload } from '../../lib/media_attachments_store';
import { getMetaContent } from '../../lib/meta';
import { claimOnboardingDeck } from '../../lib/onboardingCardDismissal';
import { cn } from '../../lib/utils';

import SpinnerIcon from '../../../assets/images/spinner.svg';

import { KEYCHAIN } from '../../contexts/KeyMaster';
import { DirectMessageRouteProvider, RouteWorkspaceProvider } from '../../contexts/RouteWorkspaceContext';
import { ModalPortal } from '../modals/ModalPortal';
import WidgetStudio, { templateBlurb, templateDisplayName } from '../modals/WidgetStudio';
import { templateIconUrl } from './WidgetIcon';

const FLEXICON_IMAGE_CONTENT_TYPES = JSON.parse(getMetaContent('channel:flexicon_image_content_types') || '[]');
const WORKSPACE_FLEXICON_UPLOAD_PURPOSE = 'workspace_flexicon';

const SUBMIT_STAGE = {
  CREATING: 'creating',
  IDLE: 'idle',
  UPLOADING: 'uploading',
};

const SUBMIT_LABEL_BY_STAGE = {
  [SUBMIT_STAGE.CREATING]: 'Creating',
  [SUBMIT_STAGE.IDLE]: 'Create',
  [SUBMIT_STAGE.UPLOADING]: 'Uploading image',
};

function defaultWorkspaceUrlForWorkspace(workspace) {
  const workspaceId = String(workspace.id);
  const mainChannelId = workspace.main_channel_id ? String(workspace.main_channel_id) : null;
  return mainChannelId ? `/spaces/${workspaceId}/chats/${mainChannelId}` : `/spaces/${workspaceId}`;
}

function fileIsAllowed(file) {
  return !FLEXICON_IMAGE_CONTENT_TYPES.length || FLEXICON_IMAGE_CONTENT_TYPES.includes(file?.type);
}

function workspaceErrorMessage(data) {
  const titleErrors = data?.validations?.title;
  if (Array.isArray(titleErrors) && titleErrors.length > 0) return `Name ${titleErrors[0]}`;
  return 'Could not create group. Please try again.';
}

const WorkspacePhotoPicker = React.memo(function WorkspacePhotoPicker({ previewUrl, isDragActive, isLoading }) {
  return (
    <span className="relative flex size-30 items-center justify-center rounded-full">
      {previewUrl ? (
        <img src={previewUrl} alt="" className="size-full rounded-full border-[0.5px] border-black/10 object-cover" />
      ) : (
        <>
          <span aria-hidden className="absolute inset-0 rounded-full border border-dashed border-idle-blue" />
          <span className="text-[13px] leading-5 text-idle-blue">Photo</span>
        </>
      )}
      {isDragActive ? <span className="absolute inset-0 rounded-full bg-black/35" aria-hidden /> : null}
      {isLoading ? (
        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45" aria-hidden>
          <SpinnerIcon className="size-5 animate-spin text-white" />
        </span>
      ) : null}
      {/* Same chip as FlexiconEditor's overlay badge (chat settings); hover rides the picker button's group. */}
      <span className="pointer-events-none absolute right-0 bottom-0 flex size-9 items-center justify-center rounded-full bg-black/40 text-white shadow-[0px_4px_4px_rgba(0,0,0,0.02)] backdrop-blur-[10px] group-hover:bg-black/60">
        <Upload size={18} strokeWidth={1.5} absoluteStrokeWidth />
      </span>
    </span>
  );
});

// The whole card opens the widget library on this card's template (same row-div +
// inner-button pattern as ThreadListRow); the chip toggles the selection directly —
// both end in the same checked state.
const WidgetCard = React.memo(function WidgetCard({ slug, title, description, iconUrl, isSelected, onOpenLibrary, onToggle }) {
  const handleCardClick = useCallback(() => onOpenLibrary(slug), [onOpenLibrary, slug]);
  const handleToggleClick = useCallback(
    (event) => {
      event.stopPropagation();
      onToggle(slug);
    },
    [onToggle, slug],
  );

  return (
    <div
      onClick={handleCardClick}
      className="flex cursor-pointer flex-col gap-3 rounded-lg border-[0.5px] border-white bg-gray-50 px-3 pt-2 pb-3 hover:bg-white"
    >
      <div className="flex items-center justify-between">
        <span className="flex size-9 items-center justify-center">
          {iconUrl ? <img src={iconUrl} alt="" className="size-9 object-contain" draggable="false" /> : null}
        </span>
        {/* Centering lives on the inner span, not the button — buttons are
            unreliable grid/flex containers in Safari and can seat children
            off-axis. The span is a plain element, so place-items-center is exact. */}
        <button
          type="button"
          onClick={handleToggleClick}
          aria-label={isSelected ? `Remove ${title}` : `Add ${title}`}
          aria-pressed={isSelected}
          className={cn(
            // transition-none (self + descendants) as in SelectionIndicator: without it
            // the freshly-mounted Check inherits the black→white color mid-transition
            // and flashes dark on the black circle.
            'block size-7 shrink-0 cursor-pointer rounded-full p-0 transition-none [&_*]:transition-none',
            isSelected ? 'bg-black text-white hover:bg-neutral-800' : 'bg-gray-200 text-black hover:bg-gray-300',
          )}
        >
          <span className="grid size-full place-items-center">
            {isSelected ? (
              <Check size={18} strokeWidth={1.5} absoluteStrokeWidth />
            ) : (
              <Plus size={18} strokeWidth={1.5} absoluteStrokeWidth />
            )}
          </span>
        </button>
      </div>
      <span className="text-[13px] leading-5 font-semibold text-black">{title}</span>
      <p className="text-[13px] leading-[18px] text-gray-500">{description}</p>
    </div>
  );
});

const NewWorkspacePanel = ({
  workspaceUrlForWorkspace = defaultWorkspaceUrlForWorkspace,
  onWorkspaceCreated = null,
  // The workspace whose template catalog backs the widget library while browsing
  // (the group being created doesn't exist yet). Null → no library; the chips
  // still toggle selections directly. On DM routes the context must come from
  // DirectMessageRouteProvider (same split as RetainedWorkspacePaneEntry).
  libraryWorkspaceId = null,
  libraryDirectMessageId = null,
}) => {
  const [name, setName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitStage, setSubmitStage] = useState(SUBMIT_STAGE.IDLE);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedWidgetSlugs, setSelectedWidgetSlugs] = useState(() => new Set());
  const [widgetLibrarySlug, setWidgetLibrarySlug] = useState(null);
  const [widgetTemplates, setWidgetTemplates] = useState([]);
  const fileInputRef = useRef(null);

  // The cards mirror the library's template catalog (fetched fresh each time the
  // modal mounts), so new templates show up here without a code change.
  useEffect(
    function loadWidgetTemplateCards() {
      if (!libraryWorkspaceId) {
        setWidgetTemplates([]);
        return undefined;
      }

      const abortController = new AbortController();

      async function loadTemplates() {
        try {
          const query = new URLSearchParams({ workspace_id: libraryWorkspaceId });
          const response = await apiGet(`/widget_templates?${query.toString()}`, { signal: abortController.signal });
          const data = await response.json();

          if (abortController.signal.aborted) return;
          if (!(response.ok && data.ok && Array.isArray(data.data?.widget_templates))) {
            throw new Error(responseErrorMessage(data, 'Could not load widget templates.'));
          }

          setWidgetTemplates(data.data.widget_templates);
        } catch (error) {
          if (error?.name === 'AbortError') return;
          logError('NewWorkspacePanel', 'Failed to load widget templates', { error });
        }
      }

      loadTemplates();

      return () => abortController.abort();
    },
    [libraryWorkspaceId],
  );

  const handleToggleWidgetSlug = useCallback(function handleToggleWidgetSlugCallback(slug) {
    setSelectedWidgetSlugs((previous) => {
      const next = new Set(previous);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  const handleOpenWidgetLibrary = useCallback(
    function handleOpenWidgetLibraryCallback(slug) {
      // Without a browsing workspace the library can't load templates; fall back
      // to toggling the card directly, same end state as the chip.
      if (!libraryWorkspaceId) {
        handleToggleWidgetSlug(slug);
        return;
      }
      setWidgetLibrarySlug(slug);
    },
    [handleToggleWidgetSlug, libraryWorkspaceId],
  );

  const handleCloseWidgetLibrary = useCallback(function handleCloseWidgetLibraryCallback() {
    setWidgetLibrarySlug(null);
  }, []);

  const isSubmitting = submitStage !== SUBMIT_STAGE.IDLE;
  const isImageLoading = Boolean(selectedFile) && isSubmitting;
  const trimmedName = name.trim();

  useEffect(
    function buildPreviewUrl() {
      if (!selectedFile) {
        setPreviewUrl(null);
        return undefined;
      }

      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);

      return function revokePreviewUrl() {
        URL.revokeObjectURL(url);
      };
    },
    [selectedFile],
  );

  const handleNameChange = useCallback(function handleNameChangeCallback(event) {
    setName(event.target.value);
    setErrorMessage(null);
  }, []);

  const handleFile = useCallback(function handleFileCallback(file) {
    if (!file) return;

    if (!fileIsAllowed(file)) {
      setErrorMessage('Choose an image file.');
      return;
    }

    setSelectedFile(file);
    setErrorMessage(null);
  }, []);

  const handleFileSelect = useCallback(
    function handleFileSelectCallback(event) {
      handleFile(event.target.files?.[0]);
      event.target.value = '';
    },
    [handleFile],
  );

  const handleFileButtonClick = useCallback(function handleFileButtonClickCallback() {
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    function handleDropCallback(acceptedFiles) {
      handleFile(acceptedFiles[0]);
    },
    [handleFile],
  );

  const handleDropRejected = useCallback(function handleDropRejectedCallback() {
    setErrorMessage('Choose an image file.');
  }, []);

  const dropzoneAccept = useMemo(function buildDropzoneAccept() {
    return Object.fromEntries(FLEXICON_IMAGE_CONTENT_TYPES.map((type) => [type, []]));
  }, []);

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    multiple: false,
    disabled: isSubmitting,
    accept: dropzoneAccept,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
  });

  const submitLabel = SUBMIT_LABEL_BY_STAGE[submitStage];

  const uploadWorkspaceImage = useCallback(async function uploadWorkspaceImageCallback(workspaceId, file) {
    const uploadId = await enqueueUpload({
      file,
      purpose: WORKSPACE_FLEXICON_UPLOAD_PURPOSE,
      workspaceId,
      sessionKey: `new-workspace:${workspaceId}:flexicon`,
    });

    try {
      const attachment = await waitForMediaAttachmentUpload(uploadId);
      return { attachmentId: attachment.attachment_id, uploadId };
    } catch (error) {
      await removeMediaAttachment(uploadId);
      throw error;
    }
  }, []);

  // Best-effort: the group already exists by now, so a failed install logs and
  // moves on rather than blocking the redirect into the new chat.
  const installSelectedWidgetTemplates = useCallback(async function installSelectedWidgetTemplatesCallback(workspace, slugs) {
    const workspaceId = String(workspace.id);
    const channelId = workspace.main_channel_id ? String(workspace.main_channel_id) : null;
    if (!channelId) return;

    let installedAny = false;

    for (const slug of slugs) {
      try {
        const response = await apiPost(`/widget_templates/${encodeURIComponent(slug)}/install`, {
          widget_template: { workspace_id: workspaceId, channel_id: channelId },
        });
        const data = await response.json();

        if (!(response.ok && data.ok)) throw new Error(responseErrorMessage(data, 'Could not install widget.'));
        if (data.data?.widget) await db.widgets.put(data.data.widget);
        installedAny = true;
      } catch (error) {
        logError('NewWorkspacePanel', 'Failed to install selected widget template', { slug, error });
      }
    }

    if (installedAny) {
      await clearFingerprint('widgets');
      await clearFingerprint('board_items');
    }
  }, []);

  const updateWorkspaceImage = useCallback(async function updateWorkspaceImageCallback(workspaceId, attachmentId) {
    const response = await apiPatchAsUser(`/workspaces/${workspaceId}`, {
      workspace: { flexicon_type: 'image', flexicon_id: attachmentId },
    });
    const data = await response.json();

    if (!(response.ok && data.ok)) throw new Error('workspace-image-update-failed');

    return data.data?.workspace || null;
  }, []);

  const handleSubmit = useCallback(
    async function handleSubmitCallback(event) {
      event.preventDefault();
      if (isSubmitting) return;

      if (!trimmedName) {
        setErrorMessage('Name is required.');
        return;
      }

      setSubmitStage(SUBMIT_STAGE.CREATING);
      setErrorMessage(null);

      let workspace = null;
      let createdWorkspacePayload = null;
      let redirectTo = null;

      try {
        const response = await apiPostAsUser('/workspaces', {
          workspace: { title: trimmedName },
        });
        const data = await response.json();

        if (!(response.ok && data.ok && data.data?.workspace?.id)) {
          setErrorMessage(workspaceErrorMessage(data));
          return;
        }

        createdWorkspacePayload = data.data;
        workspace = createdWorkspacePayload.workspace;
        // Creating the group is what earns the onboarding deck: everyone who joins a
        // standard workspace is an admin (temporary all-admin default), so the deck
        // gates on this claim rather than on role — invitees never create, never claim.
        claimOnboardingDeck(String(workspace.id), createdWorkspacePayload.profile?.id ? String(createdWorkspacePayload.profile.id) : null);

        if (selectedFile) {
          setSubmitStage(SUBMIT_STAGE.UPLOADING);
          const { attachmentId, uploadId } = await uploadWorkspaceImage(workspace.id, selectedFile);

          try {
            const updatedWorkspace = await updateWorkspaceImage(workspace.id, attachmentId);
            if (updatedWorkspace) {
              workspace = updatedWorkspace;
              createdWorkspacePayload = { ...createdWorkspacePayload, workspace: updatedWorkspace };
            }
          } finally {
            await removeMediaAttachment(uploadId);
          }
        }

        if (selectedWidgetSlugs.size > 0) {
          await installSelectedWidgetTemplates(workspace, selectedWidgetSlugs);
        }

        if (workspace?.id) redirectTo = workspaceUrlForWorkspace(workspace);
      } catch (error) {
        if (workspace?.id) {
          logError('NewWorkspacePanel', 'Workspace created but image upload failed', { workspaceId: workspace.id, error });
          redirectTo = workspaceUrlForWorkspace(workspace);
        } else {
          logError('NewWorkspacePanel', 'Failed to create workspace', error);
          setErrorMessage('Could not create group. Please try again.');
          return;
        }
      } finally {
        if (!redirectTo) setSubmitStage(SUBMIT_STAGE.IDLE);
      }

      if (!redirectTo) return;

      if (!onWorkspaceCreated) {
        assignDocument(redirectTo, 'workspace-switch');
        return;
      }

      try {
        await onWorkspaceCreated(createdWorkspacePayload, redirectTo);
      } catch (error) {
        logError('NewWorkspacePanel', 'Failed to activate created workspace without reload', error);
        assignDocument(redirectTo, 'workspace-switch');
      }
    },
    [
      installSelectedWidgetTemplates,
      isSubmitting,
      onWorkspaceCreated,
      selectedFile,
      selectedWidgetSlugs,
      workspaceUrlForWorkspace,
      trimmedName,
      updateWorkspaceImage,
      uploadWorkspaceImage,
    ],
  );

  // The library browses the current context's template catalog; in selection
  // mode "Add to chat" toggles the checked set instead of installing anything.
  // One element, wrapped below in whichever route provider the context needs.
  const widgetLibrary = (
    <WidgetStudio
      selectionMode
      selectedTemplateSlugs={selectedWidgetSlugs}
      onToggleTemplate={handleToggleWidgetSlug}
      initialTemplateSlug={widgetLibrarySlug}
      onClose={handleCloseWidgetLibrary}
    />
  );

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col">
      {/* Floats at the top bar line like SettingsBackButton (its right-side mirror);
          anchored to the Modal root, so it stays put while ModalContent scrolls. */}
      <button
        type="submit"
        disabled={isSubmitting || !trimmedName}
        className="absolute top-5 right-5 z-(--stack-modal-close) flex h-9 cursor-pointer items-center justify-center rounded-full bg-idle-blue px-4 text-[15px] leading-5 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
      >
        {submitLabel}
      </button>

      <div>
        <p className="pt-6 text-center text-[13px] leading-5 text-gray-500">Invite your guests after the group is created</p>

        <div {...getRootProps()} className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={handleFileButtonClick}
            disabled={isSubmitting}
            className="group cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-gray-500 disabled:cursor-wait"
            aria-label="Upload group photo"
          >
            <WorkspacePhotoPicker previewUrl={previewUrl} isDragActive={isDragActive} isLoading={isImageLoading} />
          </button>
        </div>

        <div className="mt-8 flex flex-col gap-2">
          <label htmlFor="new-group-name" className="text-[13px] leading-5 text-black">
            Group name
          </label>
          <input
            id="new-group-name"
            type="text"
            value={name}
            onChange={handleNameChange}
            autoFocus
            required
            data-1p-ignore="true"
            className={cn(
              'h-12 w-full rounded-lg border border-white bg-gray-50 px-4 text-[15px] leading-5 text-black outline-none hover:bg-white focus:border-gray-400 sm:border-[0.5px]',
              errorMessage && !trimmedName && 'border-red-300 focus:border-red-400',
            )}
          />
          {errorMessage ? <div className="text-xs leading-5 font-medium text-red-600">{errorMessage}</div> : null}
        </div>

        {widgetTemplates.length > 0 ? (
          <div className="mt-8 flex flex-col gap-2">
            <span className="text-[13px] leading-5 text-black">Add widgets</span>
            <div className="grid grid-cols-2 gap-2.5">
              {widgetTemplates.map((template) => (
                <WidgetCard
                  key={template.slug}
                  slug={template.slug}
                  title={templateDisplayName(template)}
                  description={templateBlurb(template)}
                  iconUrl={templateIconUrl(template)}
                  isSelected={selectedWidgetSlugs.has(template.slug)}
                  onOpenLibrary={handleOpenWidgetLibrary}
                  onToggle={handleToggleWidgetSlug}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={FLEXICON_IMAGE_CONTENT_TYPES.join(',')}
        onChange={handleFileSelect}
        className="hidden"
      />

      <ModalPortal
        open={Boolean(widgetLibrarySlug && libraryWorkspaceId)}
        onClose={handleCloseWidgetLibrary}
        size="browser"
        className="h-[80dvh] max-h-[calc(100dvh-32px)] w-full max-w-[1065px]"
        keychainId={KEYCHAIN.MODAL__CREATE_WIDGET}
      >
        {libraryWorkspaceId ? (
          // DM routes hydrate through the DM provider — RouteWorkspaceProvider
          // would wait forever on a workspace row that only exists as a direct
          // message.
          libraryDirectMessageId ? (
            <DirectMessageRouteProvider directMessageId={libraryDirectMessageId} includeEntitySnapshots={false}>
              {widgetLibrary}
            </DirectMessageRouteProvider>
          ) : (
            <RouteWorkspaceProvider workspaceId={libraryWorkspaceId} includeEntitySnapshots={false}>
              {widgetLibrary}
            </RouteWorkspaceProvider>
          )
        ) : null}
      </ModalPortal>
    </form>
  );
};

export default NewWorkspacePanel;
